/** This file must only contain pure code and pure imports */

import { InternalTextureSource, InternalTexture } from "../../Materials/Textures/internalTexture";
import { Logger } from "../../Misc/logger";
import { type Nullable } from "../../types";
import { type Scene } from "../../scene.pure";
import { LoadImage } from "../../Misc/fileTools.pure";
import { RandomGUID } from "../../Misc/guid";
import { type IWebRequest } from "../../Misc/interfaces/iWebRequest";
import { _GetCompatibleTextureLoader } from "core/Materials/Textures/Loaders/textureLoaderManager";
import { GetExtensionFromUrl } from "core/Misc/urlTools";
import { AbstractEngine } from "../abstractEngine.pure";

let _Registered = false;
/**
 * Register side effects for abstractEngineCubeTexture.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterAbstractEngineCubeTexture(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    AbstractEngine.prototype._partialLoadFile = function (
        url: string,
        index: number,
        loadedFiles: ArrayBuffer[],
        onfinish: (files: ArrayBuffer[]) => void,
        onErrorCallBack: Nullable<(message?: string, exception?: any) => void> = null
    ): void {
        const onload = (data: ArrayBuffer) => {
            loadedFiles[index] = data;
            (<any>loadedFiles)._internalCount++;

            if ((<any>loadedFiles)._internalCount === 6) {
                onfinish(loadedFiles);
            }
        };

        const onerror = (request?: IWebRequest, exception?: any) => {
            if (onErrorCallBack && request) {
                onErrorCallBack(request.status + " " + request.statusText, exception);
            }
        };

        this._loadFile(url, onload as (data: string | ArrayBuffer) => void, undefined, undefined, true, onerror);
    };

    AbstractEngine.prototype._cascadeLoadFiles = function (
        scene: Nullable<Scene>,
        onfinish: (images: ArrayBuffer[]) => void,
        files: string[],
        onError: Nullable<(message?: string, exception?: any) => void> = null
    ): void {
        const loadedFiles: ArrayBuffer[] = [];
        (<any>loadedFiles)._internalCount = 0;

        for (let index = 0; index < 6; index++) {
            this._partialLoadFile(files[index], index, loadedFiles, onfinish, onError);
        }
    };

    AbstractEngine.prototype._cascadeLoadImgs = function (
        scene: Nullable<Scene>,
        texture: InternalTexture,
        onfinish: Nullable<(texture: InternalTexture, images: HTMLImageElement[] | ImageBitmap[]) => void>,
        files: string[],
        onError: Nullable<(message?: string, exception?: any) => void> = null,
        mimeType?: string
    ) {
        const loadedImages: HTMLImageElement[] | ImageBitmap[] = [];
        (<any>loadedImages)._internalCount = 0;

        for (let index = 0; index < 6; index++) {
            this._partialLoadImg(files[index], index, loadedImages, scene, texture, onfinish, onError, mimeType);
        }
    };

    AbstractEngine.prototype._partialLoadImg = function (
        url: string,
        index: number,
        loadedImages: HTMLImageElement[] | ImageBitmap[],
        scene: Nullable<Scene>,
        texture: InternalTexture,
        onfinish: Nullable<(texture: InternalTexture, images: HTMLImageElement[] | ImageBitmap[]) => void>,
        onErrorCallBack: Nullable<(message?: string, exception?: any) => void> = null,
        mimeType?: string
    ) {
        const tokenPendingData = RandomGUID();

        const onload = (img: HTMLImageElement | ImageBitmap) => {
            loadedImages[index] = img;
            (<any>loadedImages)._internalCount++;

            if (scene) {
                scene.removePendingData(tokenPendingData);
            }

            if ((<any>loadedImages)._internalCount === 6 && onfinish) {
                onfinish(texture, loadedImages);
            }
        };

        const onerror = (message?: string, exception?: any) => {
            if (scene) {
                scene.removePendingData(tokenPendingData);
            }

            if (onErrorCallBack) {
                onErrorCallBack(message, exception);
            }
        };

        LoadImage(url, onload, onerror, scene ? scene.offlineProvider : null, mimeType);
        if (scene) {
            scene.addPendingData(tokenPendingData);
        }
    };

    AbstractEngine.prototype.createCubeTextureBase = function (
        rootUrl: string,
        scene: Nullable<Scene>,
        files: Nullable<string[]>,
        noMipmap?: boolean,
        onLoad: Nullable<(data?: any) => void> = null,
        onError: Nullable<(message?: string, exception?: any) => void> = null,
        format?: number,
        forcedExtension: any = null,
        createPolynomials: boolean = false,
        lodScale: number = 0,
        lodOffset: number = 0,
        fallback: Nullable<InternalTexture> = null,
        beforeLoadCubeDataCallback: Nullable<(texture: InternalTexture, data: ArrayBufferView | ArrayBufferView[]) => void> = null,
        imageHandler: Nullable<(texture: InternalTexture, imgs: HTMLImageElement[] | ImageBitmap[]) => void> = null,
        useSRGBBuffer = false,
        buffer: Nullable<ArrayBufferView> = null
    ): InternalTexture {
        const texture = fallback ? fallback : new InternalTexture(this, InternalTextureSource.Cube);
        texture.isCube = true;
        texture.url = rootUrl;
        texture.generateMipMaps = !noMipmap;
        texture._lodGenerationScale = lodScale;
        texture._lodGenerationOffset = lodOffset;
        texture._useSRGBBuffer = !!useSRGBBuffer && this._caps.supportSRGBBuffers && (this.version > 1 || this.isWebGPU || !!noMipmap);
        if (texture !== fallback) {
            texture.label = rootUrl.substring(0, 60); // default label, can be overriden by the caller
        }

        if (!this._doNotHandleContextLost) {
            texture._extension = forcedExtension;
            texture._files = files;
            texture._buffer = buffer;
        }

        const originalRootUrl = rootUrl;
        if (this._transformTextureUrl && !fallback) {
            rootUrl = this._transformTextureUrl(rootUrl);
        }

        const extension = forcedExtension ?? GetExtensionFromUrl(rootUrl);

        const loaderPromise = _GetCompatibleTextureLoader(extension);

        const localOnError = (message?: string, exception?: any) => {
            // if an error was thrown during load, dispose the texture, otherwise it will stay in the cache
            texture.dispose();
            if (onError) {
                onError(message, exception);
            } else if (message) {
                Logger.Warn(message);
            }
        };

        const onInternalError = (request?: IWebRequest, exception?: any) => {
            if (rootUrl === originalRootUrl) {
                if (request) {
                    localOnError(request.status + " " + request.statusText, exception);
                }
            } else {
                // fall back to the original url if the transformed url fails to load
                Logger.Warn(`Failed to load ${rootUrl}, falling back to the ${originalRootUrl}`);
                this.createCubeTextureBase(
                    originalRootUrl,
                    scene,
                    files,
                    !!noMipmap,
                    onLoad,
                    localOnError,
                    format,
                    forcedExtension,
                    createPolynomials,
                    lodScale,
                    lodOffset,
                    texture,
                    beforeLoadCubeDataCallback,
                    imageHandler,
                    useSRGBBuffer,
                    buffer
                );
            }
        };

        if (loaderPromise) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises, github/no-then
            loaderPromise.then((loader) => {
                const onLoadData = (data: ArrayBufferView | ArrayBufferView[]) => {
                    if (beforeLoadCubeDataCallback) {
                        beforeLoadCubeDataCallback(texture, data);
                    }
                    loader.loadCubeData(data, texture, createPolynomials, onLoad, (message?: string, exception?: any) => {
                        localOnError(message, exception);
                    });
                };
                if (buffer) {
                    onLoadData(buffer);
                } else if (files && files.length === 6) {
                    if (loader.supportCascades) {
                        this._cascadeLoadFiles(scene, (images) => onLoadData(images.map((image) => new Uint8Array(image))), files, localOnError);
                    } else {
                        localOnError("Textures type does not support cascades.");
                    }
                } else {
                    this._loadFile(
                        rootUrl,
                        (data) => onLoadData(new Uint8Array(data as ArrayBuffer)),
                        undefined,
                        scene ? scene.offlineProvider || null : undefined,
                        true,
                        onInternalError
                    );
                }
            });
        } else {
            if (!files || files.length === 0) {
                throw new Error("Cannot load cubemap because files were not defined, or the correct loader was not found.");
            }

            this._cascadeLoadImgs(
                scene,
                texture,
                (texture: InternalTexture, imgs: HTMLImageElement[] | ImageBitmap[]) => {
                    if (imageHandler) {
                        imageHandler(texture, imgs);
                    }
                },
                files,
                localOnError
            );
        }

        this._internalTexturesCache.push(texture);

        return texture;
    };
}
